import React from 'react';

/**
 * Stripe-style login ribbon (perf split):
 * - Soft wash (masks + feGaussianBlur + grain) is baked once to a bitmap, then
 *   GPU-composited / crossfaded — no live blur re-raster while animating.
 * - Hard stroke overlays stay live SVG with SMIL path morphs for definition.
 */

const WASH_SVG_A = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 900" width="1440" height="900" overflow="hidden" color-interpolation-filters="sRGB">

<svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" overflow="hidden" color-interpolation-filters="sRGB">
  <defs>
    <filter id="loginMaskSoft" x="-30%" y="-30%" width="160%" height="160%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="44"/>
    </filter>
    <filter id="loginMaskMid" x="-22%" y="-22%" width="144%" height="144%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="22"/>
    </filter>
    <filter id="loginLayerSoft" x="-18%" y="-18%" width="136%" height="136%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="34"/>
    </filter>
    <filter id="loginLayerSoftTight" x="-12%" y="-12%" width="124%" height="124%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="18"/>
    </filter>
    <filter id="loginGrain" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" result="n"/>
      <feColorMatrix type="saturate" values="0" in="n" result="g"/>
      <feComponentTransfer in="g" result="a">
        <feFuncA type="linear" slope="0.12"/>
      </feComponentTransfer>
      <feBlend in="SourceGraphic" in2="a" mode="soft-light"/>
    </filter>

    <!-- Soft silhouette only — hard definition comes from stroke overlays -->
    <mask id="loginRibbonMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g class="login-mask-soft" filter="url(#loginMaskSoft)" opacity="0.7">
        <path
          fill="#fff"
          d="
            M 540 -40
            C 700 80, 820 160, 960 240
            C 1100 320, 1240 400, 1400 520
            C 1500 600, 1540 700, 1560 860
            L 1560 1000
            L 860 1000
            C 820 840, 760 700, 700 580
            C 620 440, 520 340, 460 220
            C 410 130, 450 20, 540 -40
            Z
          "
        />
      </g>
      <g class="login-mask-mid" filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 700 -80
            C 840 30, 960 100, 1100 180
            C 1240 260, 1360 340, 1500 440
            C 1580 500, 1620 600, 1640 760
            L 1640 1000
            L 1040 1000
            C 1000 860, 960 760, 920 680
            C 860 560, 780 480, 700 390
            C 620 300, 580 200, 580 80
            C 580 -10, 640 -70, 700 -80
            Z
          "
        />
      </g>
    </mask>

    <!-- Clip strokes to ribbon, but keep stroke edges crisp (no blur) -->
    <mask id="loginStrokeMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 680 -60
            C 840 40, 980 120, 1140 210
            C 1300 300, 1440 400, 1560 540
            C 1620 620, 1640 720, 1640 860
            L 1640 1000
            L 1000 1000
            C 960 840, 900 720, 840 620
            C 760 480, 660 380, 600 280
            C 550 200, 560 80, 620 0
            C 650 -40, 660 -70, 680 -60
            Z
          "
        />
      </g>
    </mask>

    <linearGradient id="loginGradBase" x1="35%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #9ad0ff)"/>
      <stop offset="40%" stop-color="var(--login-ribbon-violet, #635bff)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #f0a8d4)"/>
    </linearGradient>
    <linearGradient id="loginGradWarm" x1="60%" y1="0%" x2="100%" y2="70%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff7a59)"/>
      <stop offset="45%" stop-color="var(--login-ribbon-lilac, #ff8fbf)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #7a5cff)"/>
    </linearGradient>
    <radialGradient id="loginGradHot" cx="78%" cy="18%" r="42%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff8a5c)" stop-opacity="1"/>
      <stop offset="55%" stop-color="var(--login-ribbon-lilac, #ff9ec8)" stop-opacity="0.7"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="loginGradCool" cx="52%" cy="42%" r="38%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #8ec8ff)" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>

    <!-- Stroke paints: fade along ribbon direction -->
    <linearGradient id="loginStrokeScreen" gradientUnits="userSpaceOnUse" x1="560" y1="40" x2="1520" y2="860">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
      <stop offset="18%" stop-color="var(--login-ribbon-sky, #c8e8ff)" stop-opacity="0.55"/>
      <stop offset="42%" stop-color="#ffffff" stop-opacity="0.82"/>
      <stop offset="68%" stop-color="var(--login-ribbon-lilac, #ffd0ea)" stop-opacity="0.55"/>
      <stop offset="88%" stop-color="var(--login-ribbon-peach, #ffc2b0)" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="loginStrokeBurn" gradientUnits="userSpaceOnUse" x1="620" y1="0" x2="1480" y2="900">
      <stop offset="0%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
      <stop offset="30%" stop-color="var(--login-ribbon-violet, #4f46e5)" stop-opacity="0.55"/>
      <stop offset="60%" stop-color="var(--login-ribbon-peach, #e85d3a)" stop-opacity="0.45"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #d946a0)" stop-opacity="0"/>
    </linearGradient>
  </defs>
<!-- Soft wash fill -->
  <g class="login-art-wash" mask="url(#loginRibbonMask)" filter="url(#loginGrain)">
    <rect class="login-art-layer login-art-layer--base" width="1440" height="900" fill="url(#loginGradBase)" opacity="0.9"/>
    <g class="login-art-layer login-art-layer--a" filter="url(#loginLayerSoft)">
      <ellipse cx="1180" cy="180" rx="460" ry="320" fill="url(#loginGradHot)" opacity="1"/>
      <ellipse cx="1280" cy="420" rx="400" ry="360" fill="url(#loginGradWarm)" opacity="0.85"/>
    </g>
    <g class="login-art-layer login-art-layer--b" filter="url(#loginLayerSoft)">
      <ellipse cx="820" cy="300" rx="320" ry="280" fill="url(#loginGradCool)" opacity="0.9"/>
      <ellipse cx="1000" cy="620" rx="420" ry="300" fill="var(--login-ribbon-violet, #635bff)" opacity="0.55"/>
    </g>
    <g class="login-art-layer login-art-layer--c" filter="url(#loginLayerSoftTight)">
      <ellipse cx="1320" cy="120" rx="240" ry="180" fill="var(--login-ribbon-peach, #ff7a59)" opacity="0.8"/>
      <ellipse cx="1120" cy="760" rx="360" ry="260" fill="var(--login-ribbon-lilac, #ff9ec8)" opacity="0.5"/>
      <ellipse cx="740" cy="200" rx="200" ry="190" fill="var(--login-ribbon-cyan, #7ec8ea)" opacity="0.55"/>
    </g>
  </g>
</svg>`;

const WASH_SVG_B = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 900" width="1440" height="900" overflow="hidden" color-interpolation-filters="sRGB">

<svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" overflow="hidden" color-interpolation-filters="sRGB">
  <defs>
    <filter id="loginMaskSoft" x="-30%" y="-30%" width="160%" height="160%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="44"/>
    </filter>
    <filter id="loginMaskMid" x="-22%" y="-22%" width="144%" height="144%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="22"/>
    </filter>
    <filter id="loginLayerSoft" x="-18%" y="-18%" width="136%" height="136%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="34"/>
    </filter>
    <filter id="loginLayerSoftTight" x="-12%" y="-12%" width="124%" height="124%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="18"/>
    </filter>
    <filter id="loginGrain" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" result="n"/>
      <feColorMatrix type="saturate" values="0" in="n" result="g"/>
      <feComponentTransfer in="g" result="a">
        <feFuncA type="linear" slope="0.12"/>
      </feComponentTransfer>
      <feBlend in="SourceGraphic" in2="a" mode="soft-light"/>
    </filter>

    <!-- Soft silhouette only — hard definition comes from stroke overlays -->
    <mask id="loginRibbonMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g class="login-mask-soft" filter="url(#loginMaskSoft)" opacity="0.7">
        <path
          fill="#fff"
          d="
            M 540 -40
            C 700 80, 820 160, 960 240
            C 1100 320, 1240 400, 1400 520
            C 1500 600, 1540 700, 1560 860
            L 1560 1000
            L 860 1000
            C 820 840, 760 700, 700 580
            C 620 440, 520 340, 460 220
            C 410 130, 450 20, 540 -40
            Z
          "
        />
      </g>
      <g class="login-mask-mid" filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 700 -80
            C 840 30, 960 100, 1100 180
            C 1240 260, 1360 340, 1500 440
            C 1580 500, 1620 600, 1640 760
            L 1640 1000
            L 1040 1000
            C 1000 860, 960 760, 920 680
            C 860 560, 780 480, 700 390
            C 620 300, 580 200, 580 80
            C 580 -10, 640 -70, 700 -80
            Z
          "
        />
      </g>
    </mask>

    <!-- Clip strokes to ribbon, but keep stroke edges crisp (no blur) -->
    <mask id="loginStrokeMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 680 -60
            C 840 40, 980 120, 1140 210
            C 1300 300, 1440 400, 1560 540
            C 1620 620, 1640 720, 1640 860
            L 1640 1000
            L 1000 1000
            C 960 840, 900 720, 840 620
            C 760 480, 660 380, 600 280
            C 550 200, 560 80, 620 0
            C 650 -40, 660 -70, 680 -60
            Z
          "
        />
      </g>
    </mask>

    <linearGradient id="loginGradBase" x1="35%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #9ad0ff)"/>
      <stop offset="40%" stop-color="var(--login-ribbon-violet, #635bff)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #f0a8d4)"/>
    </linearGradient>
    <linearGradient id="loginGradWarm" x1="60%" y1="0%" x2="100%" y2="70%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff7a59)"/>
      <stop offset="45%" stop-color="var(--login-ribbon-lilac, #ff8fbf)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #7a5cff)"/>
    </linearGradient>
    <radialGradient id="loginGradHot" cx="78%" cy="18%" r="42%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff8a5c)" stop-opacity="1"/>
      <stop offset="55%" stop-color="var(--login-ribbon-lilac, #ff9ec8)" stop-opacity="0.7"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="loginGradCool" cx="52%" cy="42%" r="38%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #8ec8ff)" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>

    <!-- Stroke paints: fade along ribbon direction -->
    <linearGradient id="loginStrokeScreen" gradientUnits="userSpaceOnUse" x1="560" y1="40" x2="1520" y2="860">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
      <stop offset="18%" stop-color="var(--login-ribbon-sky, #c8e8ff)" stop-opacity="0.55"/>
      <stop offset="42%" stop-color="#ffffff" stop-opacity="0.82"/>
      <stop offset="68%" stop-color="var(--login-ribbon-lilac, #ffd0ea)" stop-opacity="0.55"/>
      <stop offset="88%" stop-color="var(--login-ribbon-peach, #ffc2b0)" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="loginStrokeBurn" gradientUnits="userSpaceOnUse" x1="620" y1="0" x2="1480" y2="900">
      <stop offset="0%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
      <stop offset="30%" stop-color="var(--login-ribbon-violet, #4f46e5)" stop-opacity="0.55"/>
      <stop offset="60%" stop-color="var(--login-ribbon-peach, #e85d3a)" stop-opacity="0.45"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #d946a0)" stop-opacity="0"/>
    </linearGradient>
  </defs>
<!-- Soft wash fill -->
  <g class="login-art-wash" mask="url(#loginRibbonMask)" filter="url(#loginGrain)">
    <rect class="login-art-layer login-art-layer--base" width="1440" height="900" fill="url(#loginGradBase)" opacity="0.9"/>
    <g class="login-art-layer login-art-layer--a" filter="url(#loginLayerSoft)">
      <ellipse cx="1140" cy="210" rx="460" ry="320" fill="url(#loginGradHot)" opacity="1"/>
      <ellipse cx="1320" cy="390" rx="400" ry="360" fill="url(#loginGradWarm)" opacity="0.85"/>
    </g>
    <g class="login-art-layer login-art-layer--b" filter="url(#loginLayerSoft)">
      <ellipse cx="860" cy="270" rx="320" ry="280" fill="url(#loginGradCool)" opacity="0.9"/>
      <ellipse cx="970" cy="650" rx="420" ry="300" fill="var(--login-ribbon-violet, #635bff)" opacity="0.55"/>
    </g>
    <g class="login-art-layer login-art-layer--c" filter="url(#loginLayerSoftTight)">
      <ellipse cx="1290" cy="150" rx="240" ry="180" fill="var(--login-ribbon-peach, #ff7a59)" opacity="0.8"/>
      <ellipse cx="1090" cy="790" rx="360" ry="260" fill="var(--login-ribbon-lilac, #ff9ec8)" opacity="0.5"/>
      <ellipse cx="710" cy="230" rx="200" ry="190" fill="var(--login-ribbon-cyan, #7ec8ea)" opacity="0.55"/>
    </g>
  </g>
</svg>`;

const STROKE_SVG = `<svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" overflow="hidden" color-interpolation-filters="sRGB">

<svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" overflow="hidden" color-interpolation-filters="sRGB">
  <defs>
    <filter id="loginMaskSoft" x="-30%" y="-30%" width="160%" height="160%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="44"/>
    </filter>
    <filter id="loginMaskMid" x="-22%" y="-22%" width="144%" height="144%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="22"/>
    </filter>
    <filter id="loginLayerSoft" x="-18%" y="-18%" width="136%" height="136%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="34"/>
    </filter>
    <filter id="loginLayerSoftTight" x="-12%" y="-12%" width="124%" height="124%" color-interpolation-filters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="18"/>
    </filter>
    <filter id="loginGrain" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch" result="n"/>
      <feColorMatrix type="saturate" values="0" in="n" result="g"/>
      <feComponentTransfer in="g" result="a">
        <feFuncA type="linear" slope="0.12"/>
      </feComponentTransfer>
      <feBlend in="SourceGraphic" in2="a" mode="soft-light"/>
    </filter>

    <!-- Soft silhouette only — hard definition comes from stroke overlays -->
    <mask id="loginRibbonMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g class="login-mask-soft" filter="url(#loginMaskSoft)" opacity="0.7">
        <path
          fill="#fff"
          d="
            M 540 -40
            C 700 80, 820 160, 960 240
            C 1100 320, 1240 400, 1400 520
            C 1500 600, 1540 700, 1560 860
            L 1560 1000
            L 860 1000
            C 820 840, 760 700, 700 580
            C 620 440, 520 340, 460 220
            C 410 130, 450 20, 540 -40
            Z
          "
        />
      </g>
      <g class="login-mask-mid" filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 700 -80
            C 840 30, 960 100, 1100 180
            C 1240 260, 1360 340, 1500 440
            C 1580 500, 1620 600, 1640 760
            L 1640 1000
            L 1040 1000
            C 1000 860, 960 760, 920 680
            C 860 560, 780 480, 700 390
            C 620 300, 580 200, 580 80
            C 580 -10, 640 -70, 700 -80
            Z
          "
        />
      </g>
    </mask>

    <!-- Clip strokes to ribbon, but keep stroke edges crisp (no blur) -->
    <mask id="loginStrokeMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1440" height="900">
      <rect width="1440" height="900" fill="#000"/>
      <g filter="url(#loginMaskMid)">
        <path
          fill="#fff"
          d="
            M 680 -60
            C 840 40, 980 120, 1140 210
            C 1300 300, 1440 400, 1560 540
            C 1620 620, 1640 720, 1640 860
            L 1640 1000
            L 1000 1000
            C 960 840, 900 720, 840 620
            C 760 480, 660 380, 600 280
            C 550 200, 560 80, 620 0
            C 650 -40, 660 -70, 680 -60
            Z
          "
        />
      </g>
    </mask>

    <linearGradient id="loginGradBase" x1="35%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #9ad0ff)"/>
      <stop offset="40%" stop-color="var(--login-ribbon-violet, #635bff)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #f0a8d4)"/>
    </linearGradient>
    <linearGradient id="loginGradWarm" x1="60%" y1="0%" x2="100%" y2="70%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff7a59)"/>
      <stop offset="45%" stop-color="var(--login-ribbon-lilac, #ff8fbf)"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #7a5cff)"/>
    </linearGradient>
    <radialGradient id="loginGradHot" cx="78%" cy="18%" r="42%">
      <stop offset="0%" stop-color="var(--login-ribbon-peach, #ff8a5c)" stop-opacity="1"/>
      <stop offset="55%" stop-color="var(--login-ribbon-lilac, #ff9ec8)" stop-opacity="0.7"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="loginGradCool" cx="52%" cy="42%" r="38%">
      <stop offset="0%" stop-color="var(--login-ribbon-sky, #8ec8ff)" stop-opacity="0.95"/>
      <stop offset="100%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
    </radialGradient>

    <!-- Stroke paints: fade along ribbon direction -->
    <linearGradient id="loginStrokeScreen" gradientUnits="userSpaceOnUse" x1="560" y1="40" x2="1520" y2="860">
      <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
      <stop offset="18%" stop-color="var(--login-ribbon-sky, #c8e8ff)" stop-opacity="0.55"/>
      <stop offset="42%" stop-color="#ffffff" stop-opacity="0.82"/>
      <stop offset="68%" stop-color="var(--login-ribbon-lilac, #ffd0ea)" stop-opacity="0.55"/>
      <stop offset="88%" stop-color="var(--login-ribbon-peach, #ffc2b0)" stop-opacity="0.4"/>
      <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="loginStrokeBurn" gradientUnits="userSpaceOnUse" x1="620" y1="0" x2="1480" y2="900">
      <stop offset="0%" stop-color="var(--login-ribbon-violet, #635bff)" stop-opacity="0"/>
      <stop offset="30%" stop-color="var(--login-ribbon-violet, #4f46e5)" stop-opacity="0.55"/>
      <stop offset="60%" stop-color="var(--login-ribbon-peach, #e85d3a)" stop-opacity="0.45"/>
      <stop offset="100%" stop-color="var(--login-ribbon-lilac, #d946a0)" stop-opacity="0"/>
    </linearGradient>

  </defs>
<!--
    Thicker ribbon strokes, tripled cheaply:
    - 6 morphing masters
    - companions are unique jittered curves (no SMIL) — irregular spacing + path variance
  -->
  <g class="login-art-strokes login-art-strokes--synced" mask="url(#loginStrokeMask)">
    <g class="login-art-strokes--screen">
      <!-- Band A: leading edge -->
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="40" stroke-linecap="round" stroke-linejoin="round"
          d="M 690 -40 C 620 80, 560 180, 580 300 C 610 450, 700 540, 800 640 C 900 740, 980 860, 1020 1000">
          <animate attributeName="d" dur="12s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 690 -40 C 620 80, 560 180, 580 300 C 610 450, 700 540, 800 640 C 900 740, 980 860, 1020 1000;M 730 -70 C 610 40, 500 220, 620 340 C 690 490, 760 580, 890 690 C 980 790, 1040 910, 1080 1000;M 690 -40 C 620 80, 560 180, 580 300 C 610 450, 700 540, 800 640 C 900 740, 980 860, 1020 1000"/>
        </path>
        <!-- close jitter -->
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="28.8" stroke-linecap="round" opacity="0.55"
          d="M 724 -28 C 648 95, 602 205, 642 315 C 690 455, 770 555, 870 650 C 950 750, 1010 870, 1055 1000"/>
        <!-- wide jitter, different undulation phase -->
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="25.6" stroke-linecap="round" opacity="0.4"
          d="M 640 10 C 560 130, 500 250, 540 380 C 590 520, 650 600, 760 720 C 850 820, 910 910, 940 1000"/>
      </g>

      <!-- Band B -->
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="35.2" stroke-linecap="round" opacity="0.9"
          d="M 780 20 C 720 140, 700 250, 760 360 C 840 500, 960 560, 1080 680 C 1180 780, 1280 880, 1360 980">
          <animate attributeName="d" dur="14s" begin="-2s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 780 20 C 720 140, 700 250, 760 360 C 840 500, 960 560, 1080 680 C 1180 780, 1280 880, 1360 980;M 820 -20 C 700 110, 640 290, 820 400 C 960 560, 1060 620, 1220 760 C 1320 860, 1400 940, 1460 1010;M 780 20 C 720 140, 700 250, 760 360 C 840 500, 960 560, 1080 680 C 1180 780, 1280 880, 1360 980"/>
        </path>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="27.2" stroke-linecap="round" opacity="0.5"
          d="M 812 5 C 750 155, 745 270, 825 385 C 915 520, 1035 585, 1165 700 C 1255 800, 1340 900, 1410 990"/>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="24" stroke-linecap="round" opacity="0.38"
          d="M 730 55 C 670 175, 640 295, 700 420 C 780 560, 900 640, 1040 760 C 1140 850, 1220 930, 1280 1000"/>
      </g>

      <!-- Band C -->
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="32" stroke-linecap="round" opacity="0.78"
          d="M 900 60 C 860 180, 860 290, 940 400 C 1040 540, 1180 600, 1300 720 C 1380 800, 1460 900, 1500 980">
          <animate attributeName="d" dur="11s" begin="-4s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 900 60 C 860 180, 860 290, 940 400 C 1040 540, 1180 600, 1300 720 C 1380 800, 1460 900, 1500 980;M 950 20 C 870 150, 790 330, 1000 450 C 1160 590, 1280 650, 1420 790 C 1490 870, 1540 950, 1570 1020;M 900 60 C 860 180, 860 290, 940 400 C 1040 540, 1180 600, 1300 720 C 1380 800, 1460 900, 1500 980"/>
        </path>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="25.6" stroke-linecap="round" opacity="0.48"
          d="M 948 42 C 900 195, 910 305, 1005 415 C 1110 545, 1240 620, 1365 740 C 1435 820, 1500 910, 1540 995"/>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="22.4" stroke-linecap="round" opacity="0.36"
          d="M 850 95 C 800 210, 790 330, 860 455 C 950 590, 1100 680, 1240 790 C 1330 870, 1400 940, 1445 1000"/>
      </g>

      <!-- Band D: trailing edge -->
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="28.8" stroke-linecap="round" opacity="0.62"
          d="M 1180 -10 C 1240 90, 1320 180, 1400 300 C 1480 420, 1540 560, 1580 720 C 1600 820, 1605 900, 1605 980">
          <animate attributeName="d" dur="13s" begin="-1s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 1180 -10 C 1240 90, 1320 180, 1400 300 C 1480 420, 1540 560, 1580 720 C 1600 820, 1605 900, 1605 980;M 1120 30 C 1200 130, 1260 230, 1360 370 C 1440 510, 1500 650, 1540 820 C 1560 900, 1570 960, 1575 1010;M 1180 -10 C 1240 90, 1320 180, 1400 300 C 1480 420, 1540 560, 1580 720 C 1600 820, 1605 900, 1605 980"/>
        </path>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="23.2" stroke-linecap="round" opacity="0.42"
          d="M 1225 -5 C 1285 100, 1375 205, 1455 330 C 1520 460, 1565 600, 1595 760 C 1610 850, 1615 920, 1618 990"/>
        <path fill="none" stroke="url(#loginStrokeScreen)" stroke-width="20.8" stroke-linecap="round" opacity="0.32"
          d="M 1110 25 C 1170 130, 1240 230, 1330 360 C 1410 500, 1470 640, 1510 800 C 1530 880, 1540 940, 1545 1000"/>
      </g>
    </g>

    <g class="login-art-strokes--burn">
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="36.8" stroke-linecap="round"
          d="M 740 90 C 680 210, 680 320, 760 430 C 860 570, 1000 630, 1140 750 C 1240 840, 1320 930, 1360 1000">
          <animate attributeName="d" dur="15s" begin="-3s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 740 90 C 680 210, 680 320, 760 430 C 860 570, 1000 630, 1140 750 C 1240 840, 1320 930, 1360 1000;M 790 50 C 680 180, 620 360, 820 470 C 980 620, 1120 680, 1280 820 C 1380 910, 1440 980, 1480 1040;M 740 90 C 680 210, 680 320, 760 430 C 860 570, 1000 630, 1140 750 C 1240 840, 1320 930, 1360 1000"/>
        </path>
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="27.2" stroke-linecap="round" opacity="0.55"
          d="M 778 75 C 710 220, 730 335, 830 445 C 940 575, 1080 650, 1220 770 C 1310 860, 1380 940, 1420 1005"/>
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="24" stroke-linecap="round" opacity="0.4"
          d="M 690 120 C 630 240, 610 360, 690 490 C 780 630, 930 720, 1080 830 C 1180 910, 1250 970, 1290 1020"/>
      </g>
      <g class="login-art-stroke-band">
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="30.4" stroke-linecap="round" opacity="0.8"
          d="M 980 40 C 940 160, 960 280, 1060 400 C 1180 550, 1320 620, 1440 760 C 1500 840, 1540 920, 1560 1000">
          <animate attributeName="d" dur="12.5s" begin="-5s" repeatCount="indefinite" calcMode="spline" keyTimes="0;0.5;1" keySplines="0.45 0 0.55 1;0.45 0 0.55 1"
            values="M 980 40 C 940 160, 960 280, 1060 400 C 1180 550, 1320 620, 1440 760 C 1500 840, 1540 920, 1560 1000;M 1040 0 C 960 130, 880 320, 1120 450 C 1300 600, 1420 670, 1540 830 C 1580 910, 1600 970, 1610 1030;M 980 40 C 940 160, 960 280, 1060 400 C 1180 550, 1320 620, 1440 760 C 1500 840, 1540 920, 1560 1000"/>
        </path>
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="24" stroke-linecap="round" opacity="0.5"
          d="M 1025 28 C 980 175, 1010 295, 1130 415 C 1250 555, 1380 640, 1495 775 C 1545 855, 1575 930, 1590 1005"/>
        <path fill="none" stroke="url(#loginStrokeBurn)" stroke-width="21.6" stroke-linecap="round" opacity="0.35"
          d="M 920 70 C 870 190, 870 320, 960 450 C 1070 600, 1230 700, 1370 820 C 1450 890, 1500 950, 1525 1000"/>
      </g>
    </g>
  </g>
</svg>`;


const ART_RASTER_WIDTH = 1440;
const ART_RASTER_HEIGHT = 900;

/** Narrow / phone-class viewports — animated ribbon is too heavy here. */
const STATIC_VIEWPORT_MAX_WIDTH = 768;

const MOBILE_UA =
  /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i;

const RIBBON_VARS = [
  '--login-ribbon-sky',
  '--login-ribbon-violet',
  '--login-ribbon-lilac',
  '--login-ribbon-peach',
  '--login-ribbon-cyan',
] as const;

/**
 * Prefer a static ribbon on mobile, narrow viewports, reduced-motion,
 * Save-Data, or modest CPU/memory — SMIL + dual wash bake thrash phones.
 */
const preferStaticLoginArt = (): boolean => {
  if (typeof window === 'undefined') return true;

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return true;
    }
  } catch {
    /* ignore */
  }

  const width = window.innerWidth || document.documentElement?.clientWidth || 0;
  if (width > 0 && width < STATIC_VIEWPORT_MAX_WIDTH) return true;

  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  if (!nav) return false;

  const connection = (nav as Navigator & {
    connection?: { saveData?: boolean };
  }).connection;
  if (connection?.saveData) return true;

  const cores =
    typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency > 0
      ? nav.hardwareConcurrency
      : null;
  const memoryGb =
    typeof (nav as Navigator & { deviceMemory?: number }).deviceMemory === 'number'
      ? (nav as Navigator & { deviceMemory?: number }).deviceMemory!
      : null;
  const isMobileUa = MOBILE_UA.test(nav.userAgent ?? '');

  if (cores !== null && cores <= 2) return true;
  if (memoryGb !== null && memoryGb <= 2) return true;
  // Phones / tablets (incl. mobile Safari) — skip animated SMIL + dual wash.
  if (isMobileUa) return true;

  return false;
};

/** Drop SMIL morphs — CSS alone cannot stop <animate> path updates. */
const stripSmilAnimate = (svg: string) =>
  svg.replace(/<animate\b[^>]*\/>|<animate\b[^>]*>[\s\S]*?<\/animate>/gi, '');

const coverScale = (width: number, height: number) => {
  if (width <= 0 || height <= 0) return 1;
  return Math.max(width / ART_RASTER_WIDTH, height / ART_RASTER_HEIGHT);
};

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('login wash bake failed'));
    img.src = url;
  });

const resolveRibbonSvg = (svg: string, host: HTMLElement) => {
  const themeRoot =
    (host.closest('.login-root, .login-art-shell') as HTMLElement | null) || host;
  const cs = getComputedStyle(themeRoot);
  let out = svg;
  for (const name of RIBBON_VARS) {
    const value = cs.getPropertyValue(name).trim();
    if (!value) continue;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    out = out.replace(
      new RegExp(`var\\(${escaped},\\s*#[0-9a-fA-F]+\\)`, 'g'),
      value,
    );
  }
  return out;
};

const bakeSvgToDataUrl = async (svg: string, options?: { lite?: boolean }) => {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImage(objectUrl);
    const dprCap = options?.lite ? 1 : 1.5;
    const baseScale = options?.lite ? 0.55 : 0.85;
    const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
    const scale = baseScale * dpr;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(ART_RASTER_WIDTH * scale));
    canvas.height = Math.max(1, Math.round(ART_RASTER_HEIGHT * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    try {
      return canvas.toDataURL('image/webp', options?.lite ? 0.82 : 0.92);
    } catch {
      return canvas.toDataURL('image/png');
    }
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};


/** Tip→base: width 0 at start, full width by this fraction of path length. */
const GRASS_TAPER_FRACTION = 0.75;
const GRASS_TAPER_SAMPLES = 40;

type GrassPt = { x: number; y: number };

const cubicPoint = (p0: GrassPt, p1: GrassPt, p2: GrassPt, p3: GrassPt, t: number): GrassPt => {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  return {
    x: uu * u * p0.x + 3 * uu * t * p1.x + 3 * u * tt * p2.x + tt * t * p3.x,
    y: uu * u * p0.y + 3 * uu * t * p1.y + 3 * u * tt * p2.y + tt * t * p3.y,
  };
};

const cubicTangent = (p0: GrassPt, p1: GrassPt, p2: GrassPt, p3: GrassPt, t: number): GrassPt => {
  const u = 1 - t;
  return {
    x: 3 * u * u * (p1.x - p0.x) + 6 * u * t * (p2.x - p1.x) + 3 * t * t * (p3.x - p2.x),
    y: 3 * u * u * (p1.y - p0.y) + 6 * u * t * (p2.y - p1.y) + 3 * t * t * (p3.y - p2.y),
  };
};

const parseCubicStrokePath = (d: string): { p0: GrassPt; curves: Array<[GrassPt, GrassPt, GrassPt]> } | null => {
  const nums = d.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi);
  if (!nums || nums.length < 8 || (nums.length - 2) % 6 !== 0) return null;
  const vals = nums.map(Number);
  const p0 = { x: vals[0], y: vals[1] };
  const curves: Array<[GrassPt, GrassPt, GrassPt]> = [];
  for (let i = 2; i < vals.length; i += 6) {
    curves.push([
      { x: vals[i], y: vals[i + 1] },
      { x: vals[i + 2], y: vals[i + 3] },
      { x: vals[i + 4], y: vals[i + 5] },
    ]);
  }
  return { p0, curves };
};

const halfWidthAt = (t: number, strokeWidth: number) => {
  if (t <= 0) return 0;
  if (t >= GRASS_TAPER_FRACTION) return strokeWidth * 0.5;
  const u = t / GRASS_TAPER_FRACTION;
  // smoothstep for a soft grass blade edge
  const s = u * u * (3 - 2 * u);
  return strokeWidth * 0.5 * s;
};

/** Build a filled outline that tapers to a point over the first 75% of the path. */
const taperedFillFromStrokePath = (d: string, strokeWidth: number): string => {
  const parsed = parseCubicStrokePath(d);
  if (!parsed || parsed.curves.length === 0) return d;

  const { p0, curves } = parsed;
  const left: GrassPt[] = [];
  const right: GrassPt[] = [];
  const segCount = curves.length;
  const samples = GRASS_TAPER_SAMPLES;

  for (let i = 0; i <= samples; i++) {
    const tPath = i / samples;
    const segF = tPath * segCount;
    const segIndex = Math.min(segCount - 1, Math.floor(segF));
    const tLocal = segCount === 1 ? tPath : segF - segIndex;
    const from =
      segIndex === 0 ? p0 : curves[segIndex - 1][2];
    const [c1, c2, to] = curves[segIndex];
    const p = cubicPoint(from, c1, c2, to, tLocal);
    let tan = cubicTangent(from, c1, c2, to, tLocal);
    let len = Math.hypot(tan.x, tan.y);
    if (len < 1e-4) {
      // Degenerate tip tangent — nudge along the segment.
      const p2 = cubicPoint(from, c1, c2, to, Math.min(1, tLocal + 0.02));
      tan = { x: p2.x - p.x, y: p2.y - p.y };
      len = Math.hypot(tan.x, tan.y) || 1;
    }
    const nx = -tan.y / len;
    const ny = tan.x / len;
    const hw = halfWidthAt(tPath, strokeWidth);
    left.push({ x: p.x + nx * hw, y: p.y + ny * hw });
    right.push({ x: p.x - nx * hw, y: p.y - ny * hw });
  }

  const outline = left.concat(right.reverse());
  const fmt = (pt: GrassPt) => `${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`;
  return `M ${fmt(outline[0])} ${outline.slice(1).map((pt) => `L ${fmt(pt)}`).join(' ')} Z`;
};

const applyGrassTaper = (svg: string): string => {
  // Convert constant-width strokes into filled blades with a long tip taper.
  return svg.replace(/<path\b([^>]*)>([\s\S]*?)<\/path>|<path\b([^>]*)\/>/g, (full, openA, inner, openB) => {
    const open = openA ?? openB ?? '';
    if (!/\bstroke=/.test(open) || !/\bd="/.test(open)) return full;
    const widthMatch = open.match(/stroke-width="([\d.]+)"/);
    const dMatch = open.match(/\bd="([^"]+)"/);
    const strokeMatch = open.match(/\bstroke="([^"]+)"/);
    if (!widthMatch || !dMatch || !strokeMatch) return full;

    const width = Number(widthMatch[1]);
    const paint = strokeMatch[1];
    const taperedD = taperedFillFromStrokePath(dMatch[1], width);

    let attrs = open
      .replace(/\bfill="none"/, '')
      .replace(/\bstroke="[^"]*"/, '')
      .replace(/\bstroke-width="[^"]*"/, '')
      .replace(/\bstroke-linecap="[^"]*"/, '')
      .replace(/\bstroke-linejoin="[^"]*"/, '')
      .replace(/\bd="[^"]*"/, `d="${taperedD}"`)
      .trim();
    attrs = `fill="${paint}" stroke="none" ${attrs}`.replace(/\s+/g, ' ');

    if (openB != null && inner == null) {
      return `<path ${attrs}/>`;
    }

    let body = inner ?? '';
    body = body.replace(
      /(<animate\b[^>]*\battributeName="d"[^>]*\bvalues=")([^"]+)(")/g,
      (_m, pre, values, post) => {
        const next = values
          .split(';')
          .map((part: string) => taperedFillFromStrokePath(part.trim(), width))
          .join(';');
        return `${pre}${next}${post}`;
      },
    );
    return `<path ${attrs}>${body}</path>`;
  });
};

const uniquifyIds = (svg: string, uid: string) =>
  svg.replace(/id="login/g, `id="${uid}-login`).replace(/url\(#login/g, `url(#${uid}-login`);

const LoginArtBackground = React.memo(() => {
  const uid = React.useId().replace(/:/g, '');
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const rasterRef = React.useRef<HTMLDivElement | null>(null);
  const washARef = React.useRef<HTMLDivElement | null>(null);
  const washBRef = React.useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = React.useState(false);
  const staticArt = React.useMemo(() => preferStaticLoginArt(), []);

  const strokeHtml = React.useMemo(() => {
    const base = staticArt ? stripSmilAnimate(STROKE_SVG) : STROKE_SVG;
    return uniquifyIds(applyGrassTaper(base), uid);
  }, [uid, staticArt]);

  React.useLayoutEffect(() => {
    const host = hostRef.current;
    const raster = rasterRef.current;
    if (!host || !raster) return;

    const applyCover = () => {
      raster.style.transform = `translate3d(-50%, -50%, 0) scale(${coverScale(
        host.clientWidth || window.innerWidth,
        host.clientHeight || window.innerHeight,
      )})`;
    };

    applyCover();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', applyCover);
      return () => window.removeEventListener('resize', applyCover);
    }

    const observer = new ResizeObserver(applyCover);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const host = hostRef.current;
    const washA = washARef.current;
    const washB = washBRef.current;
    if (!host || !washA) return;

    let cancelled = false;
    let lastKey = '';

    const bake = async () => {
      const themeRoot =
        (host.closest('.login-root, .login-art-shell') as HTMLElement | null) || host;
      const cs = getComputedStyle(themeRoot);
      const key = RIBBON_VARS.map((name) => cs.getPropertyValue(name).trim()).join('|');
      if (key === lastKey && washA.style.backgroundImage) return;
      lastKey = key;

      try {
        if (staticArt) {
          // Single lightweight wash bake — no B frame, no crossfade.
          const urlA = await bakeSvgToDataUrl(resolveRibbonSvg(WASH_SVG_A, host), {
            lite: true,
          });
          if (cancelled || !urlA) return;
          washA.style.backgroundImage = `url("${urlA}")`;
          washA.style.opacity = '1';
          if (washB) {
            washB.style.backgroundImage = '';
            washB.style.opacity = '0';
          }
          setReady(true);
          return;
        }

        if (!washB) return;
        const [urlA, urlB] = await Promise.all([
          bakeSvgToDataUrl(resolveRibbonSvg(WASH_SVG_A, host)),
          bakeSvgToDataUrl(resolveRibbonSvg(WASH_SVG_B, host)),
        ]);
        if (cancelled || !urlA || !urlB) return;
        washA.style.backgroundImage = `url("${urlA}")`;
        washB.style.backgroundImage = `url("${urlB}")`;
        setReady(true);
      } catch {
        // Keep strokes-only fallback if bake fails (e.g. restricted SVG filters).
      }
    };

    bake();

    const themeRoot =
      (host.closest('.login-root, .login-art-shell') as HTMLElement | null) || host;
    const mo = new MutationObserver(() => {
      void bake();
    });
    mo.observe(themeRoot, { attributes: true, attributeFilter: ['class'] });

    return () => {
      cancelled = true;
      mo.disconnect();
    };
  }, [staticArt]);

  return React.createElement(
    'div',
    {
      className: `login-art${ready ? ' login-art--baked' : ''}${
        staticArt ? ' login-art--static' : ''
      }`,
      'aria-hidden': true,
      ref: hostRef,
    },
    React.createElement(
      'div',
      { className: 'login-art-raster', ref: rasterRef },
      React.createElement(
        'div',
        { className: 'login-art-wash-stack' },
        React.createElement('div', {
          className: 'login-art-wash-baked login-art-wash-baked--a',
          ref: washARef,
        }),
        staticArt
          ? null
          : React.createElement('div', {
              className: 'login-art-wash-baked login-art-wash-baked--b',
              ref: washBRef,
            }),
      ),
      React.createElement('div', {
        className: 'login-art-strokes-live',
        dangerouslySetInnerHTML: { __html: strokeHtml },
      }),
    ),
  );
});

export default LoginArtBackground;
